<template>
  <div class="relative h-full pb-4 overflow-x-auto bg-white">
    <BaseTable
      v-if="filteredMaterialsDC"
      :headers="materialsDCHeaders"
      :list="filteredMaterialsDC"
      table-id="materials-dc-tab"
      clickable
      hide-grouping
      is-saving-layout-hidden
      :is-add-new-hidden="!isFromManager || mode === 'view'"
      :disable-multiselect="!isFromManager || mode === 'view'"
      :total-records="totalRecords"
      @add-new="onAddNew"
      @table-item-clicked="onItemClick"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="onRefreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager && mode === 'edit'"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item)" />
        </div>
      </template>
    </BaseTable>

    <MaterialsDCModalCreateEdit
      v-if="createEditModalActive"
      v-model="modifiedMaterialDC"
      :is-modified="isModalModified"
      :mode="createEditModalMode"
      :title="modalEditCreateTitle()"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import { deepCopy } from '@/utils/data';
import { sortData } from '@/utils/table';
import MaterialsDCModalCreateEdit from '@/components/documents/MaterialsDCModalCreateEdit.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';

import type { DistributionChannel } from '@/types/DistributionChannel';
import type { MaterialDC } from '@/types/MaterialDC';
import type { TableFilterData } from '@/types/Base';
import { RecordMode } from '@/types/Base';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();

const data = defineModel<DistributionChannel>();

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  isFromManager: boolean;
  totalRecords: number;
}>();

const emit = defineEmits<{
  'refresh-data': [filterData?: TableFilterData];
  'apply-filters': [filterData: TableFilterData];
  'reset-filters': [];
}>();

const materialsDCHeaders = [
  { value: 'material.name', text: $t('material.material'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const materialDC = ref<MaterialDC>();
const modifiedMaterialDC = ref<MaterialDC>();

const createEditModalActive = ref(false);

const createEditModalMode = ref<'add' | 'edit'>('add');
const isModalModified = ref<boolean>(false);

const filteredMaterialsDC = computed(() => {
  return data.value?.materialsDC?.filter((x) => x.recordMode !== RecordMode.Deleted);
});

watch(
  () => modifiedMaterialDC.value,
  (newData) => {
    isModalModified.value = JSON.stringify(newData) !== JSON.stringify(materialDC.value);
  },
  { deep: true }
);

function initializeMaterialDC(): MaterialDC {
  return {
    id: uuidv4(),
    materialID: '',
    distributionChannelID: data.value?.id || '',
    distributionChannel: {
      id: data.value?.id || '',
      company: data.value?.company || { id: '', name: '' },
      mwAccountGroup: data.value?.mwAccountGroup || { id: '', accountGroup: '', mwEntity: '' },
    },
    material: {
      id: '',
      name: '',
    },
    isActive: true,
    isFromManager: true,
    deleted: false,
  };
}

function onAddNew() {
  if (props.isFromManager && props.mode === 'edit') {
    materialDC.value = initializeMaterialDC();
    materialDC.value.recordMode = RecordMode.Added;
    modifiedMaterialDC.value = deepCopy<MaterialDC>(materialDC.value);
    createEditModalActive.value = true;
    createEditModalMode.value = 'add';
  }
}

function onItemClick(item: MaterialDC) {
  if (props.isFromManager && item.isFromManager && props.mode === 'edit') {
    materialDC.value = item;
    if (materialDC.value.recordMode !== RecordMode.Added)
      materialDC.value.recordMode = RecordMode.Modified;
    modifiedMaterialDC.value = deepCopy<MaterialDC>(materialDC.value);
    createEditModalActive.value = true;
    createEditModalMode.value = 'edit';
  }
}

async function onSave() {
  if (!data.value || !modifiedMaterialDC.value) return;

  if (!Array.isArray(data.value.materialsDC)) data.value.materialsDC = [];

  // Material info should already be set in modifiedMaterialDC from the modal
  // No need to lookup again

  // Find if this item already exists in the array
  const index = data.value.materialsDC.findIndex(
    (existingItem: any) => existingItem.id === modifiedMaterialDC.value?.id
  );

  if (index !== -1) {
    // Update existing item - ensure recordMode is Modified if not already Added
    if (modifiedMaterialDC.value.recordMode !== RecordMode.Added) {
      modifiedMaterialDC.value.recordMode = RecordMode.Modified;
    }
    data.value.materialsDC[index] = modifiedMaterialDC.value;
  } else {
    // New item - set recordMode to Added
    modifiedMaterialDC.value.recordMode = RecordMode.Added;
    data.value.materialsDC.push(modifiedMaterialDC.value);
  }

  materialDC.value = deepCopy<MaterialDC>(modifiedMaterialDC.value);
  createEditModalActive.value = false;
  createEditModalMode.value = 'add';
}

async function onDeleteMultiselect(items: MaterialDC[]) {
  if (props.isFromManager) {
    const itemsArray = Array.isArray(items) ? items : [items];
    const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
    if (itemsToDelete.length === 0) {
      toastStore.error({ text: 'Selected items cannot be deleted.' });
      return;
    }

    itemsToDelete.forEach(async (item) => {
      deleteRowItem.value = item;
      await onDeleteSingle();
    });
  }
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!data.value || !deleteRowItem.value) return;

  if (!Array.isArray(data.value.materialsDC)) return;

  const index = data.value.materialsDC.findIndex(
    (existingItem: any) => existingItem.id === deleteRowItem.value?.id
  );

  if (index !== -1) {
    const itemToDelete = data.value.materialsDC[index];
    if (itemToDelete.recordMode === RecordMode.Added) {
      // Remove completely if it was just added
      data.value.materialsDC.splice(index, 1);
    } else {
      // Mark as deleted if it exists in DB
      itemToDelete.recordMode = RecordMode.Deleted;
      itemToDelete.deleted = true;
    }
  }

  deleteRowItem.value = undefined;
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<MaterialDC>();

function onDeleteRow(item: MaterialDC) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (materialDC.value && !materialDC.value.isFromManager)
    return `${$t('common.actions.view')} ${$t('material.materialDC')} `;
  return createEditModalMode.value === 'edit'
    ? `${$t('common.actions.edit')} ${$t('material.materialDC')}`
    : `${$t('common.actions.create')} ${$t('material.materialDC')}`;
}

function onRefreshData(filterData: TableFilterData) {
  if (!filterData.query) {
    emit('reset-filters');
    return;
  }
  if (filterData.column !== undefined && filterData.order && filteredMaterialsDC.value) {
    const sorted = sortData(
      [...filteredMaterialsDC.value],
      filterData.column,
      filterData.order,
      materialsDCHeaders
    );
    if (data.value) {
      const deletedItems =
        data.value.materialsDC?.filter((x) => x.recordMode === RecordMode.Deleted) || [];
      data.value.materialsDC = [...sorted, ...deletedItems];
    }
    return;
  }

  emit('refresh-data', filterData);
}

function onApplyFilters(filterData: TableFilterData) {
  emit('apply-filters', filterData);
}

function onResetFilters() {
  emit('reset-filters');
}
</script>
